import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiCog } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './CurrencyIndex.scss';

interface Currency {
  id: number;
  name: string;
  symbol: string;
  hedge: number;
  lastExchangeRate: string;
  lastExchangeRateDate?: string | null;
  lastExchangeRateSource?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const CurrencyIndex: React.FC = () => {
  const { t } = useTranslation(['currencies', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [useBankExchangeRate, setUseBankExchangeRate] = useState(false);
  const [fetchOtherRates, setFetchOtherRates] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingOther, setUpdatingOther] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchCurrencies = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/Currency?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('currencies:messages.fetchError'));
        return;
      }

      setCurrencies(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('currencies:messages.fetchError'));
      console.error('Error fetching currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await adminApi.call('/config', 'GET');

      if (!response.success) {
        console.error("Error fetching config:", response.error);
        return;
      }

      const data = response.data;
      const bankExchangeRateConfig = data.items?.find((item: any) => item.name === "UseBankExchangeRateInsteadOfAPI");
      if (bankExchangeRateConfig) {
        setUseBankExchangeRate(bankExchangeRateConfig.value === "true" || bankExchangeRateConfig.value === true);
      }

      const fetchOtherRatesConfig = data.items?.find((item: any) => item.name === "FetchOtherExchangeRatesUsingAPI");
      if (fetchOtherRatesConfig) {
        setFetchOtherRates(fetchOtherRatesConfig.value === "true" || fetchOtherRatesConfig.value === true);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const updateBankExchangeRateSetting = async () => {
    setUpdating(true);

    try {
      const data = [{
        name: "UseBankExchangeRateInsteadOfAPI",
        value: (!useBankExchangeRate).toString(),
      }];

      const response = await adminApi.call('/config', 'POST', data);

      if (!response.success) {
        throw new Error(response.error || "Failed to update configuration");
      }

      setUseBankExchangeRate(!useBankExchangeRate);
      showSuccessToast(`Bank exchange rate setting ${!useBankExchangeRate ? 'enabled' : 'disabled'} successfully!`);
    } catch (error: any) {
      showErrorToast(`Error: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const updateFetchOtherRatesSetting = async () => {
    setUpdatingOther(true);

    try {
      const data = [{
        name: "FetchOtherExchangeRatesUsingAPI",
        value: (!fetchOtherRates).toString(),
      }];

      const response = await adminApi.call('/config', 'POST', data);

      if (!response.success) {
        throw new Error(response.error || "Failed to update configuration");
      }

      setFetchOtherRates(!fetchOtherRates);
      showSuccessToast(`Fetch other exchange rates setting ${!fetchOtherRates ? 'enabled' : 'disabled'} successfully!`);
    } catch (error: any) {
      showErrorToast(`Error: ${error.message}`);
    } finally {
      setUpdatingOther(false);
    }
  };

  useEffect(() => {
    fetchCurrencies(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    fetchConfig();
  }, []);

  const deleteCurrency = async (id: number) => {
    if (!window.confirm(t('currencies:messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await adminApi.call(`/Currency/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('currencies:messages.deleteError'));
        return;
      }

      showSuccessToast(t('currencies:messages.deleteSuccess'));
      fetchCurrencies(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('currencies:messages.deleteError'));
      console.error('Error deleting currency:', error);
    }
  };

  const handleCreateCurrency = () => {
    navigate('/Currencies/create');
  };

  const handleEditCurrency = (id: number) => {
    navigate(`/Currencies/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('currencies:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'symbol',
      title: t('currencies:table.symbol'),
      sortable: true,
      filterable: true,
      filterKey: 'symbol',
    },
    {
      key: 'hedge',
      title: t('currencies:table.hedge'),
      sortable: true,
      // render: (value) => typeof value === 'number' ? value.toFixed(4) : '-',
    },
    {
      key: 'lastExchangeRate',
      title: t('currencies:table.lastExchangeRate'),
      sortable: true,
      // render: (value) => typeof value === 'number' ? value.toFixed(4) : '-',
    },
    {
      key: 'lastExchangeRateDate',
      title: 'Last Exchange Date',
      sortable: true,
      render: (value: string | null) => {
        if (!value) return '-';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
      },
    },
    {
      key: 'lastExchangeRateSource',
      title: 'Exchange Source',
      sortable: true,
      render: (value: string | null) => value || '-',
    },
    {
      key: 'actions',
      title: t('currencies:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, currency) => (
        <div className="action-buttons">
          {hasPermission("Update Currency") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditCurrency(currency.id)}
              tooltip={t('currencies:editCurrency')}
            />
          )}
          {hasPermission("Delete Currency") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCurrency(currency.id)}
              tooltip={t('currencies:deleteCurrency')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('currencies:breadcrumbs.currencies') }
  ];

  const headerActions = (
    <div className="header-actions">
      {hasPermission("Update Config") && (
        <>
          <div className="config-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={useBankExchangeRate}
                onChange={updateBankExchangeRateSetting}
                disabled={updating}
              />
              <span className="toggle-text">Use Bank Exchange Rate (IQD)</span>
            </label>
            {updating && <div className="spinner-sm"></div>}
          </div>
          <div className="config-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={fetchOtherRates}
                onChange={updateFetchOtherRatesSetting}
                disabled={updatingOther}
              />
              <span className="toggle-text">Fetch Other Exchange Rates via API</span>
            </label>
            {updatingOther && <div className="spinner-sm"></div>}
          </div>
        </>
      )}
    </div>
  );

  return (
    <PageLayout
      title={t('currencies:title')}
      subtitle={t('currencies:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={currencies}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Currency") ? {
          label: t('currencies:addNew'),
          onClick: handleCreateCurrency
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCurrencies(currentPage, pageSize)}
        emptyMessage={t('currencies:messages.noCurrencies')}
      />
    </PageLayout>
  );
};
